import React, { useState, useEffect, useMemo } from "react";
import { 
  Users, Network, Upload, FileText, Search, Sparkles, Filter, Trash2, 
  Check, ArrowRight, Download, Copy, RefreshCw, Star, Info, Mail, Linkedin, Calendar,
  Globe, UserCheck, ShieldAlert, ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

export default function NetworkConnectorPage() {
  const { user, subscription } = useAuth();
  const isPro = subscription?.plan_name === "STUDLYF Pro" && subscription?.status === "active";
  
  // Form states
  const [sector, setSector] = useState("Healthcare AI");
  const [idea, setIdea] = useState("AI platform for early cancer detection");
  const [stage, setStage] = useState("MVP");
  const [goal, setGoal] = useState("Looking for hospitals for pilot testing");
  const [preferredType, setPreferredType] = useState("Doctor");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  
  // Connection states
  const [connections, setConnections] = useState([]);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [importSummary, setImportSummary] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  // Persistent Bookmark state
  const [bookmarkedContacts, setBookmarkedContacts] = useState(() => {
    try {
      const saved = localStorage.getItem("bookmarkedContacts");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("bookmarkedContacts", JSON.stringify(bookmarkedContacts));
  }, [bookmarkedContacts]);

  // Active Filter states
  const [filterSearch, setFilterSearch] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("All");
  const [filterLocation, setFilterLocation] = useState("All");
  const [filterRole, setFilterRole] = useState("All"); 
  const [minExperience, setMinExperience] = useState(0);
  const [minMatchScore, setMinMatchScore] = useState(50);
  
  // Single Card Navigator index state
  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset current recommendation index whenever matches or filters change
  useEffect(() => {
    setCurrentIndex(0);
  }, [analysisResults, filterSearch, filterIndustry, filterLocation, filterRole, minMatchScore, minExperience]);
  
  // Selected Match card for AI Generation
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [outreachTone, setOutreachTone] = useState("Professional");
  const [outreachChannel, setOutreachChannel] = useState("LinkedIn");
  const [generatedMessage, setGeneratedMessage] = useState("");
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Fetch connections on mount
  const fetchConnections = async () => {
    setLoading(true);
    try {
      const res = await api.get("/workspace/network-connector/connections");
      setConnections(res.data);
    } catch (e) {
      console.error("Could not fetch connections", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  // Recalculate analysis silently in real-time post-initial-analysis
  const runSilentAnalysis = async () => {
    if (connections.length === 0) {
      setAnalysisResults([]);
      return;
    }
    try {
      const res = await api.post("/workspace/network-connector/analyze", {
        sector,
        idea,
        stage,
        goal,
        preferred_type: preferredType
      });
      setAnalysisResults(res.data.recommendations || []);
      setDebugInfo(res.data.debug || null);
      if (res.data.debug) {
        console.log("--- NETWORK CONNECTOR DEBUG INFO ---");
        console.log(`Imported Connections: ${res.data.debug.imported}`);
        console.log(`Valid Connections: ${res.data.debug.valid}`);
        console.log(`Analyzed Connections: ${res.data.debug.analyzed}`);
        console.log(`Scored Connections: ${res.data.debug.scored}`);
        console.log(`Highest Score: ${res.data.debug.highest_score}`);
        console.log(`Lowest Score: ${res.data.debug.lowest_score}`);
        console.log(`Average Score: ${res.data.debug.average_score}`);
        console.log(`Top 10 Scores: ${res.data.debug.top_10_scores?.join(", ")}`);
        console.log(`Recommendations Returned: ${res.data.debug.recommendations_returned}`);
        console.log(`Processing Time: ${res.data.debug.processing_time} seconds`);
        console.log("------------------------------------");
      }
    } catch (e) {
      console.error("Silent recalculation failed", e);
    }
  };

  useEffect(() => {
    if (hasAnalyzed) {
      runSilentAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, sector, idea, stage, goal, preferredType, hasAnalyzed]);

  // Trigger explicit validation & sequential loading steps
  const handleAnalyzeClick = async () => {
    // 1. Validate required fields
    if (!sector.trim()) {
      toast.error("Startup Sector is required.");
      return;
    }
    if (!idea.trim()) {
      toast.error("Startup Idea is required.");
      return;
    }
    if (!goal.trim()) {
      toast.error("Goal is required.");
      return;
    }
    if (!preferredType) {
      toast.error("Preferred Connection Type is required.");
      return;
    }

    // 2. Validate that contacts exist
    if (connections.length === 0) {
      toast.error("Please import your connections or load demo contacts before running AI analysis.");
      return;
    }

    // 3. Initiate analysis flow
    setAnalyzing(true);
    setHasAnalyzed(false);

    // Sequential loading steps animation
    for (let step = 1; step <= 6; step++) {
      setAnalysisStep(step);
      await new Promise((resolve) => setTimeout(resolve, 550));
    }

    try {
      const res = await api.post("/workspace/network-connector/analyze", {
        sector,
        idea,
        stage,
        goal,
        preferred_type: preferredType
      });
      setAnalysisResults(res.data.recommendations || []);
      setDebugInfo(res.data.debug || null);
      if (res.data.debug) {
        console.log("--- NETWORK CONNECTOR DEBUG INFO ---");
        console.log(`Imported Connections: ${res.data.debug.imported}`);
        console.log(`Valid Connections: ${res.data.debug.valid}`);
        console.log(`Analyzed Connections: ${res.data.debug.analyzed}`);
        console.log(`Scored Connections: ${res.data.debug.scored}`);
        console.log(`Highest Score: ${res.data.debug.highest_score}`);
        console.log(`Lowest Score: ${res.data.debug.lowest_score}`);
        console.log(`Average Score: ${res.data.debug.average_score}`);
        console.log(`Top 10 Scores: ${res.data.debug.top_10_scores?.join(", ")}`);
        console.log(`Recommendations Returned: ${res.data.debug.recommendations_returned}`);
        console.log(`Processing Time: ${res.data.debug.processing_time} seconds`);
        console.log("------------------------------------");
      }
      setHasAnalyzed(true);
      toast.success("AI network analysis completed!");
    } catch (e) {
      console.error("AI Analysis error", e);
      toast.error("Could not run AI analysis. Try checking your connections database.");
    } finally {
      setAnalyzing(false);
      setAnalysisStep(0);
    }
  };

  // Bulk import connections (Seeding helper or CRM/Manual source)
  const handleImport = async (sourceType) => {
    setImporting(true);
    toast.loading(`Importing connections from ${sourceType}...`);
    try {
      // POST with empty payload triggers the seeding list in the backend
      const res = await api.post("/workspace/network-connector/connections/import", []);
      setConnections(res.data.count ? new Array(res.data.count) : []);
      setImportSummary({
        imported: res.data.count || 35,
        valid: res.data.count || 35,
        skipped: 0
      });
      await fetchConnections();
      toast.dismiss();
      toast.success(`Successfully imported ${res.data.count || 35} network connections!`, {
        description: "Privacy permission granted. Analyzing profiles..."
      });
    } catch (e) {
      toast.dismiss();
      toast.error("Import failed. Try again.");
    } finally {
      setImporting(false);
    }
  };

  // Delete/Clear connections
  const handleDeleteConnections = async () => {
    if (!window.confirm("Are you sure you want to delete all imported network connections? This action is permanent and complies with privacy guidelines.")) return;
    setLoading(true);
    try {
      await api.delete("/workspace/network-connector/connections");
      setConnections([]);
      setAnalysisResults([]);
      setSelectedMatch(null);
      setImportSummary(null);
      setDebugInfo(null);
      setHasAnalyzed(false);
      toast.success("Successfully deleted all imported professional connections.");
    } catch (e) {
      toast.error("Could not delete connections.");
    } finally {
      setLoading(false);
    }
  };

  // AI Message generation call
  const generateMessage = async (match, forceApi = false) => {
    setSelectedMatch(match);
    setDrawerOpen(true);
    
    // If we have a pre-generated outreach message and the tone/channel are defaults, use it directly!
    if (match.suggested_outreach && outreachTone === "Professional" && outreachChannel === "LinkedIn" && !forceApi) {
      setGeneratedMessage(match.suggested_outreach);
      setGeneratingMessage(false);
      return;
    }
    
    setGeneratingMessage(true);
    try {
      const res = await api.post("/workspace/network-connector/generate-outreach", {
        contact_name: match.contact.name,
        contact_headline: match.contact.headline,
        contact_company: match.contact.company,
        contact_position: match.contact.position,
        contact_skills: match.contact.skills,
        contact_mutual_interests: match.contact.mutual_interests,
        startup_sector: sector,
        startup_idea: idea,
        startup_stage: stage,
        goal,
        tone: outreachTone,
        channel: outreachChannel
      });
      setGeneratedMessage(res.data.message);
    } catch (e) {
      toast.error("AI Generation failed. Check API config.");
    } finally {
      setGeneratingMessage(false);
    }
  };

  // Trigger regeneration on tone or channel change
  useEffect(() => {
    if (selectedMatch && drawerOpen) {
      generateMessage(selectedMatch, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachTone, outreachChannel]);

  // Copy Outreach message to Clipboard
  const copyMessage = () => {
    navigator.clipboard.writeText(generatedMessage);
    toast.success("Outreach message copied to clipboard!");
  };

  // Export Outreach as text file download
  const exportMessage = () => {
    const blob = new Blob([generatedMessage], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedMatch.contact.name.replace(/\s+/g, "_")}_outreach_${outreachChannel.toLowerCase()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Outreach message exported successfully.");
  };

  // Filters logic
  const filteredMatches = useMemo(() => {
    return analysisResults.filter((match) => {
      const c = match.contact;
      
      // Search match
      const query = filterSearch.toLowerCase();
      const matchesSearch = !query || 
        c.name.toLowerCase().includes(query) ||
        c.headline.toLowerCase().includes(query) ||
        c.company.toLowerCase().includes(query) ||
        c.skills.some(s => s.toLowerCase().includes(query));

      // Industry match
      const matchesIndustry = filterIndustry === "All" || (c.industry && c.industry === filterIndustry);

      // Location match
      const matchesLocation = filterLocation === "All" || (c.location && c.location.includes(filterLocation));

      // Role check
      let matchesRole = true;
      if (filterRole === "Investor") matchesRole = c.is_investor;
      if (filterRole === "Founder") matchesRole = c.is_founder;
      if (filterRole === "Advisor") matchesRole = c.is_advisor;

      // Score / Experience boundaries
      const matchesScore = match.match_score >= minMatchScore;
      const matchesExp = c.years_experience === undefined || c.years_experience >= minExperience;

      return matchesSearch && matchesIndustry && matchesLocation && matchesRole && matchesScore && matchesExp;
    });
  }, [analysisResults, filterSearch, filterIndustry, filterLocation, filterRole, minMatchScore, minExperience]);

  // Derived filter options
  const industriesList = useMemo(() => {
    const list = new Set(analysisResults.map(m => m.contact.industry || "").filter(Boolean));
    return ["All", ...Array.from(list)];
  }, [analysisResults]);

  const locationsList = useMemo(() => {
    const list = new Set(analysisResults.map(m => m.contact.location ? m.contact.location.split(",")[0] : "").filter(Boolean));
    return ["All", ...Array.from(list)];
  }, [analysisResults]);

  // Network insights derived metrics
  const insights = useMemo(() => {
    if (!debugInfo) return null;
    const total = debugInfo.valid;
    const relevant = debugInfo.matched;
    const topIndustry = debugInfo.top_industry || "None";
    const topLocation = debugInfo.top_location || "None";

    return {
      total,
      relevant,
      topIndustry,
      topLocation,
      strongestNetwork: topIndustry,
      weakestArea: total < 10 ? "Hospital procurement" : "Series B VC funding",
      expansionTips: "Connect with medical administrators or venture leads specializing in deep tech bioinformatics."
    };
  }, [debugInfo]);

  // Helper to parse a single CSV line with quote awareness
  const parseCSVLine = (line) => {
    const result = [];
    let current = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // LinkedIn Connections CSV Parser
  const parseLinkedInCSV = (text) => {
    const lines = text.split(/\r?\n/);
    let headerIndex = -1;
    let headers = [];
    
    // Find header row containing First Name and Last Name
    for (let i = 0; i < lines.length; i++) {
      const cols = parseCSVLine(lines[i]);
      const hasFirstName = cols.some(c => c.toLowerCase().replace(/[\s_-]/g, '') === 'firstname');
      const hasLastName = cols.some(c => c.toLowerCase().replace(/[\s_-]/g, '') === 'lastname');
      if (hasFirstName && hasLastName) {
        headerIndex = i;
        headers = cols.map(c => c.trim());
        break;
      }
    }
    
    if (headerIndex === -1) {
      throw new Error("Invalid LinkedIn CSV format: Could not find 'First Name' and 'Last Name' headers.");
    }
    
    const contacts = [];
    let skippedCount = 0;
    
    for (let i = headerIndex + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) {
        continue; // Skip empty lines
      }
      const cols = parseCSVLine(line);
      if (cols.length < 2 || cols.every(c => !c)) {
        skippedCount++;
        continue;
      }
      
      const row = {};
      headers.forEach((header, index) => {
        row[header] = cols[index] || "";
      });
      
      const findVal = (possibleKeys) => {
        for (const key of Object.keys(row)) {
          const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, '');
          if (possibleKeys.includes(normalizedKey)) {
            return row[key];
          }
        }
        return "";
      };
      
      const firstName = findVal(['firstname', 'first']);
      const lastName = findVal(['lastname', 'last']);
      const name = `${firstName} ${lastName}`.trim();
      
      if (!name || name === 'undefined undefined') {
        skippedCount++;
        continue;
      }
      
      const company = findVal(['company', 'currentcompany', 'organization']);
      const position = findVal(['position', 'jobtitle', 'title', 'role']);
      const email = findVal(['emailaddress', 'email']);
      const url = findVal(['url', 'linkedinurl', 'profileurl', 'linkedinprofileurl']);
      const industry = findVal(['industry', 'sector']);
      const location = findVal(['location', 'country', 'city', 'address']);
      
      const positionLower = position.toLowerCase();
      const isFounder = positionLower.includes('founder') || positionLower.includes('co-founder') || positionLower.includes('creator');
      const isInvestor = positionLower.includes('investor') || positionLower.includes('vc') || positionLower.includes('venture') || positionLower.includes('angel');
      const isAdvisor = positionLower.includes('advisor') || positionLower.includes('consultant') || positionLower.includes('mentor');
      
      contacts.push({
        name,
        headline: position || "Professional Connection",
        company: company || "Independent",
        position: position || "Professional",
        email: email || "",
        linkedin_url: url || "",
        is_founder: isFounder,
        is_investor: isInvestor,
        is_advisor: isAdvisor,
        industry: industry || "Technology",
        location: location || "Global",
        skills: findVal(['skills', 'expertise']) ? findVal(['skills', 'expertise']).split(/[,;]/).map(s => s.trim()) : [],
        connection_strength: 50,
        years_experience: 5
      });
    }
    
    return { contacts, skippedCount };
  };

  // Simple Seed File trigger
  const triggerCSVUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = async (event) => {
        const csvText = event.target.result;
        try {
          const { contacts, skippedCount } = parseLinkedInCSV(csvText);
          const totalCount = contacts.length + skippedCount;
          
          setImportSummary({
            imported: totalCount,
            valid: contacts.length,
            skipped: skippedCount
          });
          
          setImporting(true);
          toast.loading("Uploading LinkedIn connections...");
          const res = await api.post("/workspace/network-connector/connections/import", contacts);
          setConnections(res.data.count ? new Array(res.data.count) : []);
          await fetchConnections();
          toast.dismiss();
          toast.success(`Successfully imported ${res.data.count} network connections!`, {
            description: `Import Summary: Imported: ${totalCount} | Valid: ${contacts.length} | Skipped: ${skippedCount}`
          });
        } catch (err) {
          toast.dismiss();
          toast.error(err.message || "Failed to parse CSV file. Ensure it is a valid LinkedIn Connections export.");
        } finally {
          setImporting(false);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="space-y-6" data-testid="network-connector-page">
      {/* Header Banner */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED] flex items-center gap-1">
            <Network className="w-3.5 h-3.5 animate-pulse" /> AI Network Connector
          </p>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tighter">
            Find the Best Person in Your Network
          </h1>
          <p className="mt-2 text-gray-600 text-sm max-w-2xl">
            Describe your startup goal and analyze authorized connections to rank relevance, map network proximity, and generate custom pitches.
          </p>
        </div>
        {isPro && connections.length > 0 && (
          <button
            onClick={handleDeleteConnections}
            className="flex items-center gap-1.5 px-4.5 py-2.5 rounded-full border border-red-200 text-red-500 hover:bg-red-50 text-xs font-semibold transition"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete Connections
          </button>
        )}
      </div>

      {!isPro ? (
        <div className="rounded-3xl border border-gray-150 bg-white/40 backdrop-blur-md p-12 text-center shadow-sm relative overflow-hidden flex flex-col items-center justify-center min-h-[450px]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#7C3AED]/5 to-[#EC4899]/5 opacity-60 z-0" />
          <div className="relative z-10 max-w-md flex flex-col items-center">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-[#7C3AED]/10 to-[#EC4899]/10 text-[#7C3AED] mb-5">
              <Network className="w-10 h-10" />
            </div>
            <h2 className="text-xl font-display font-extrabold text-gray-900 tracking-tight">Unlock AI Network Connector</h2>
            <p className="text-xs text-gray-600 mt-2.5 leading-relaxed">
              Find, rank, and map the absolute best contacts in your imported LinkedIn network or CRM matching your startup's sector, idea, and funding goals.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full justify-center">
              <button
                onClick={() => window.location.href = "/workspace/settings?tab=billing"}
                className="px-6 py-3 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white font-extrabold rounded-xl text-xs hover:opacity-90 transition shadow-sm flex items-center justify-center gap-1.5"
              >
                <Sparkles className="w-3.5 h-3.5 fill-current" /> Activate Pro Access
              </button>
              <button
                onClick={() => window.location.href = "/#pricing"}
                className="px-6 py-3 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-extrabold rounded-xl text-xs transition"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Main Layout Grid */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Form & Sources Block (Span 5) */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Form Card */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-100/50 rounded-full blur-2xl pointer-events-none" />
            <h2 className="font-display font-semibold text-lg text-gray-900 mb-4 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-[#7C3AED]" /> Startup Description
            </h2>
            
            <form className="space-y-4 text-xs font-semibold text-gray-700">
              <div>
                <label className="block mb-1 text-gray-700">Startup Sector *</label>
                <input
                  type="text"
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  placeholder="e.g. Healthcare AI, SaaS, FinTech"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none font-medium text-gray-800"
                />
              </div>

              <div>
                <label className="block mb-1 text-gray-700">Startup Idea *</label>
                <textarea
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="Describe your product or solution..."
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none min-h-[60px] font-medium text-gray-800"
                />
              </div>

              <div>
                <label className="block mb-1 text-gray-700">Startup Stage</label>
                <select
                  value={stage}
                  onChange={(e) => setStage(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] outline-none bg-white font-medium text-gray-800"
                >
                  {["Idea", "Validation", "MVP", "Beta", "Early Revenue", "Growth", "Scaling"].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-gray-700">Goal / Target Contact Objective</label>
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="e.g. Looking for pilot testing hospitals, Seed VC funding"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] outline-none font-medium text-gray-800"
                />
              </div>

              <div>
                <label className="block mb-1 text-gray-700">Preferred Connection Type</label>
                <select
                  value={preferredType}
                  onChange={(e) => setPreferredType(e.target.value)}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] outline-none bg-white font-medium text-gray-800"
                >
                  {["Investor", "Founder", "Mentor", "Engineer", "Doctor", "Hospital", "Researcher", "VC", "Enterprise", "Customer", "Advisor"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block mb-1 text-gray-700">LinkedIn Profile URL (Optional)</label>
                <input
                  type="text"
                  value={linkedinUrl}
                  onChange={(e) => setLinkedinUrl(e.target.value)}
                  placeholder="https://linkedin.com/in/username"
                  className="w-full px-4 py-3 rounded-2xl border border-gray-200 focus:border-[#7C3AED] outline-none font-medium text-gray-800"
                />
              </div>
            </form>
          </div>

          {/* Connection Sources Import Card */}
          <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xs">
            <h3 className="font-display font-semibold text-base text-gray-900 mb-2 flex items-center gap-1.5">
              <Upload className="w-4.5 h-4.5 text-gray-500" /> Connections Source
            </h3>
            <p className="text-[11px] text-gray-500 leading-normal mb-4">
              We respect your data privacy. Only authorized contacts imported explicitly below will be analyzed. We never scrape or access LinkedIn without permission.
            </p>

            <div className="space-y-2">
              <label className="w-full flex items-center justify-between p-3.5 border border-gray-200 hover:border-[#7C3AED]/40 hover:bg-gray-50/50 rounded-2xl cursor-pointer transition select-none">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div className="text-left">
                    <p className="text-xs font-semibold text-gray-800">Import LinkedIn Connections CSV</p>
                    <p className="text-[10px] text-gray-400">Export connections from LinkedIn and drag here</p>
                  </div>
                </div>
                <input type="file" accept=".csv" onChange={triggerCSVUpload} className="hidden" />
              </label>

              <button
                type="button"
                onClick={() => handleImport("CRM / Contact Source")}
                disabled={importing}
                className="w-full flex items-center justify-between p-3.5 border border-gray-200 hover:border-[#7C3AED]/40 hover:bg-gray-50/50 rounded-2xl transition text-left"
              >
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Connect CRM / API Source</p>
                    <p className="text-[10px] text-gray-400">HubSpot, Salesforce, or Google Contacts</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </button>

              <button
                type="button"
                onClick={() => handleImport("Manual Contacts Upload")}
                disabled={importing}
                className="w-full flex items-center justify-between p-3.5 border border-gray-200 hover:border-[#7C3AED]/40 hover:bg-gray-50/50 rounded-2xl transition text-left"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs font-semibold text-gray-800">Manual Contacts Upload</p>
                    <p className="text-[10px] text-gray-400">Enter contacts list or load sandbox demo data</p>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300" />
              </button>
            </div>
            {importSummary && (
              <div className="mt-4 p-3.5 bg-purple-50/50 border border-purple-100 rounded-2xl text-[11px] font-semibold text-gray-700">
                <p className="text-[#7C3AED] font-bold mb-1">Import Summary</p>
                <div className="grid grid-cols-3 gap-2 text-center mt-2">
                  <div className="bg-white p-2 rounded-xl border border-purple-100/50">
                    <p className="text-gray-400 text-[9px] uppercase">Imported</p>
                    <p className="text-sm font-bold text-gray-800">{importSummary.imported}</p>
                  </div>
                  <div className="bg-white p-2 rounded-xl border border-purple-100/50">
                    <p className="text-gray-400 text-[9px] uppercase">Valid</p>
                    <p className="text-sm font-bold text-green-600">{importSummary.valid}</p>
                  </div>
                  <div className="bg-white p-2 rounded-xl border border-purple-100/50">
                    <p className="text-gray-400 text-[9px] uppercase">Skipped</p>
                    <p className="text-sm font-bold text-amber-600">{importSummary.skipped}</p>
                  </div>
                </div>
              </div>
            )}
        </div>

        {/* Analyze My Network Button */}
        <button
            type="button"
            onClick={handleAnalyzeClick}
            disabled={analyzing}
            className="glow-button w-full rounded-full py-4 text-sm font-semibold disabled:opacity-80 inline-flex items-center justify-center gap-2 mt-4 transition-all duration-200"
          >
            {analyzing ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" /> Analyze My Network
              </>
            )}
          </button>
        </div>

        {/* Right Dashboard, Graph & Matches Grid (Span 7) */}
        <div className="lg:col-span-7 space-y-6">
          
          {connections.length === 0 ? (
            /* Empty State */
            <div className="bg-white border border-gray-150 rounded-[32px] p-12 text-center shadow-xs flex flex-col items-center justify-center min-h-[500px]">
              <div className="w-16 h-16 rounded-3xl bg-[#F4F1FF] text-[#7C3AED] flex items-center justify-center mb-4">
                <Network className="w-8 h-8 animate-pulse" />
              </div>
              <h3 className="font-display font-semibold text-xl">No connections authorized yet</h3>
              <p className="text-xs text-gray-500 max-w-sm mt-2 leading-relaxed">
                Describe your startup goals and import a connections list (CSV, CRM, or click "Manual Contacts Upload" to seed demo contacts) to initiate deep semantic ranking.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <button
                  onClick={() => handleImport("Manual Seeding")}
                  className="glow-button rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2"
                >
                  <Users className="w-4 h-4" /> Load Demo Contacts
                </button>
              </div>
            </div>
          ) : !hasAnalyzed && !analyzing ? (
            /* Pending Analysis State */
            <div className="bg-white border border-gray-150 rounded-[32px] p-12 text-center shadow-xs flex flex-col items-center justify-center min-h-[500px]">
              <div className="w-16 h-16 rounded-3xl bg-[#F4F1FF] text-[#7C3AED] flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="font-display font-semibold text-xl">Ready to Analyze</h3>
              <p className="text-xs text-gray-500 max-w-sm mt-2 leading-relaxed">
                Your connection database is loaded. Click the <strong>Analyze My Network</strong> button on the left panel to begin match analysis.
              </p>
            </div>
          ) : analyzing ? (
            /* Sequential Steps Loading Animation */
            <div className="bg-white border border-gray-150 rounded-[32px] p-8 text-center shadow-xs flex flex-col items-center justify-center min-h-[500px] space-y-6">
              <div className="flex flex-col items-center space-y-2">
                <RefreshCw className="w-10 h-10 text-[#7C3AED] animate-spin" />
                <h3 className="font-display font-semibold text-lg">Running AI Analysis...</h3>
                <p className="text-xs text-gray-400">Comparing startup parameters against network connections</p>
              </div>

              <div className="w-full max-w-xs space-y-2.5 bg-gray-50 p-5 rounded-2xl border border-gray-100 text-left">
                {[
                  "Reading startup idea...",
                  "Understanding startup sector...",
                  "Analyzing imported contacts...",
                  "Calculating semantic similarity...",
                  "Ranking best connections...",
                  "Generating personalized recommendations..."
                ].map((stepText, idx) => {
                  const stepNum = idx + 1;
                  const isDone = analysisStep > stepNum;
                  const isCurrent = analysisStep === stepNum;

                  return (
                    <div key={idx} className="flex items-center gap-2.5 text-[11px] font-semibold transition-all">
                      {isDone ? (
                        <Check className="w-4 h-4 text-green-500 shrink-0" strokeWidth={3} />
                      ) : isCurrent ? (
                        <RefreshCw className="w-4 h-4 text-[#7C3AED] animate-spin shrink-0" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-gray-300 shrink-0" />
                      )}
                      <span className={isDone ? "text-gray-400 font-medium" : isCurrent ? "text-[#7C3AED]" : "text-gray-300"}>
                        {stepText}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
              {/* Network Insights Widgets Dashboard */}
              {insights && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Total Connections", val: insights.total, desc: "Explicitly authorized" },
                    { label: "AI Matches (>=80%)", val: insights.relevant, desc: "High confidence score" },
                    { label: "Top Industry Hub", val: insights.topIndustry, desc: "Strongest presence" },
                    { label: "Weakest Sector", val: insights.weakestArea, desc: "Missing strategic nodes" }
                  ].map((wid, i) => (
                    <div key={i} className="bg-white border border-gray-100 rounded-2xl p-4 shadow-xs">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{wid.label}</p>
                      <p className="font-display font-bold text-lg text-gray-900 mt-1">{wid.val}</p>
                      <p className="text-[9px] text-gray-400 mt-0.5">{wid.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {debugInfo && (
                <div className="bg-slate-900 text-slate-100 rounded-3xl p-5 border border-slate-800 shadow-sm relative overflow-hidden font-mono text-[11px] space-y-1.5">
                  <div className="flex items-center justify-between border-b border-slate-800 pb-2 mb-3">
                    <span className="text-[10px] uppercase font-bold text-purple-400">Recommendation Engine Debugger</span>
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shrink-0" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-slate-300">
                    <p>Imported Connections: <strong className="text-white">{importSummary?.imported || debugInfo.imported}</strong></p>
                    <p>Valid Connections: <strong className="text-white">{importSummary?.valid || debugInfo.valid}</strong></p>
                    <p>Analyzed Connections: <strong className="text-white">{debugInfo.analyzed}</strong></p>
                    <p>Scored Connections: <strong className="text-white">{debugInfo.scored}</strong></p>
                    <p>Highest Score: <strong className="text-white">{debugInfo.highest_score}</strong></p>
                    <p>Lowest Score: <strong className="text-white">{debugInfo.lowest_score}</strong></p>
                    <p>Average Score: <strong className="text-white">{debugInfo.average_score}</strong></p>
                    <p>Top 10 Scores: <strong className="text-white">{debugInfo.top_10_scores?.join(", ")}</strong></p>
                    <p>Recommendations Returned: <strong className="text-white">{debugInfo.recommendations_returned}</strong></p>
                    <p>Processing Time: <strong className="text-white">{debugInfo.processing_time} seconds</strong></p>
                  </div>
                </div>
              )}

              {/* Interactive SVG Network visualizer */}
              <div className="bg-white border border-gray-100 rounded-[32px] p-5 shadow-xs relative overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-55 pb-3 mb-4">
                  <div>
                    <h3 className="font-display font-semibold text-sm">Visual Network Graph</h3>
                    <p className="text-[10px] text-gray-400">Semantic matches cluster closer to your center node</p>
                  </div>
                  <span className="text-[10px] bg-green-50 text-green-600 font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                    <UserCheck className="w-3 h-3" /> Privacy Sandbox Active
                  </span>
                </div>

                <div className="w-full h-[220px] bg-gray-50 rounded-2xl flex items-center justify-center overflow-hidden border border-gray-150 relative">
                  {/* SVG Hub */}
                  <svg className="w-full h-full" style={{ minWidth: "300px" }}>
                    <defs>
                      <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#7C3AED" stopOpacity="0.2" />
                        <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
                      </radialGradient>
                    </defs>

                    {/* Center Glow */}
                    <circle cx="50%" cy="50%" r="70" fill="url(#centerGlow)" />

                    {/* Proximity rings */}
                    <circle cx="50%" cy="50%" r="45" fill="none" stroke="#7C3AED" strokeOpacity="0.1" strokeDasharray="3 3" />
                    <circle cx="50%" cy="50%" r="90" fill="none" stroke="#7C3AED" strokeOpacity="0.05" />

                    {/* Center Node (Startup) */}
                    <circle cx="50%" cy="50%" r="14" fill="#7C3AED" className="animate-pulse" />
                    <text x="50%" y="53%" textAnchor="middle" fill="white" fontSize="9px" fontWeight="bold">You</text>

                    {/* Category matching branches */}
                    {filteredMatches.slice(0, 8).map((m, index) => {
                      const angle = (index * 2 * Math.PI) / Math.min(8, filteredMatches.length);
                      // High score nodes map closer to center
                      const distance = 95 - (m.match_score * 0.45);
                      const nodeX = 50 + Math.cos(angle) * (distance / 4) + "%";
                      const nodeY = 50 + Math.sin(angle) * (distance / 2) + "%";

                      return (
                        <g key={m.contact.contact_id} className="cursor-pointer group" onClick={() => generateMessage(m)}>
                          {/* Connection Line */}
                          <line x1="50%" y1="50%" x2={nodeX} y2={nodeY} stroke="#7C3AED" strokeOpacity={m.match_score / 150} strokeWidth="1" />
                          
                          {/* Node bubble */}
                          <circle cx={nodeX} cy={nodeY} r="7" fill={m.match_score >= 85 ? "#7C3AED" : m.match_score >= 60 ? "#8B5CF6" : "#A78BFA"} stroke="white" strokeWidth="1.5" />
                          
                          {/* Text labels */}
                          <text x={nodeX} y={nodeY} dy="-10" textAnchor="middle" fill="#374151" fontSize="8px" fontWeight="semibold" className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-1">
                            {m.contact.name} ({m.match_score}%)
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
              </div>

              {/* Advanced Filters Drawer/Row */}
              <div className="bg-white border border-gray-100 rounded-3xl p-5 shadow-xs grid grid-cols-1 md:grid-cols-4 gap-3 text-[11px] font-semibold text-gray-700">
                <div>
                  <label className="block mb-1 text-gray-400">Search</label>
                  <div className="relative flex items-center">
                    <Search className="w-3 h-3 text-gray-400 absolute left-2.5" />
                    <input
                      type="text"
                      value={filterSearch}
                      onChange={(e) => setFilterSearch(e.target.value)}
                      placeholder="Name, skills..."
                      className="w-full pl-7 pr-3 py-1.5 rounded-lg border border-gray-250 font-medium bg-gray-50 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-gray-400">Industry</label>
                  <select
                    value={filterIndustry}
                    onChange={(e) => setFilterIndustry(e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg border border-gray-250 font-medium bg-gray-50 outline-none"
                  >
                    {industriesList.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-gray-400">Role Focus</label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full px-2 py-1.5 rounded-lg border border-gray-250 font-medium bg-gray-50 outline-none"
                  >
                    <option value="All">All Roles</option>
                    <option value="Investor">Investor Only</option>
                    <option value="Founder">Founder Only</option>
                    <option value="Advisor">Advisor Only</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-gray-400">Match score ({minMatchScore}%)</label>
                  <input
                    type="range"
                    min="15"
                    max="95"
                    value={minMatchScore}
                    onChange={(e) => setMinMatchScore(parseInt(e.target.value))}
                    className="w-full accent-[#7C3AED] mt-2 cursor-pointer"
                  />
                </div>
              </div>

              {/* Matched ranked recommendation single card navigator */}
              <div className="space-y-4">
                <div className="flex items-center justify-between text-xs text-gray-500 font-semibold px-1">
                  <span>Ranked Recommendation ({filteredMatches.length > 0 ? `${Math.min(currentIndex + 1, filteredMatches.length)} of ${filteredMatches.length}` : 0})</span>
                  <span>recalculated automatically</span>
                </div>

                {filteredMatches.length === 0 ? (
                  /* Empty state for filter */
                  <div className="bg-white border border-gray-100 rounded-3xl p-8 text-center shadow-xs">
                    <Info className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                    <p className="font-semibold text-xs text-gray-800">No suitable contacts were found in your uploaded LinkedIn connections.</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">Try widening your filters or reducing the match score slider.</p>
                  </div>
                ) : (() => {
                  const safeIndex = Math.min(currentIndex, filteredMatches.length - 1);
                  const m = filteredMatches[safeIndex];
                  if (!m) return null;

                  return (
                    <div className="space-y-4">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={m.contact.contact_id || safeIndex}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          className="bg-white border border-gray-100 hover:shadow-md rounded-2xl p-5 flex flex-col md:flex-row justify-between gap-4 transition duration-200 relative overflow-hidden"
                        >
                          {/* Match Score Badge & Bookmark */}
                          <div className="absolute top-4 right-4 flex items-center gap-1.5 z-10">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const cid = m.contact.contact_id;
                                const isBookmarked = bookmarkedContacts.includes(cid);
                                if (isBookmarked) {
                                  setBookmarkedContacts(bookmarkedContacts.filter(id => id !== cid));
                                  toast.success("Contact removed from bookmarks");
                                } else {
                                  setBookmarkedContacts([...bookmarkedContacts, cid]);
                                  toast.success("Contact bookmarked successfully");
                                }
                              }}
                              className={`p-1.5 rounded-lg border transition ${
                                bookmarkedContacts.includes(m.contact.contact_id)
                                  ? "bg-amber-50 border-amber-200 text-amber-500 hover:bg-amber-100"
                                  : "bg-white border-gray-250 text-gray-400 hover:border-gray-300 hover:text-gray-600"
                              }`}
                              title="Bookmark Contact"
                            >
                              <Star className="w-3.5 h-3.5 fill-current" />
                            </button>

                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                              m.match_score >= 80 ? "bg-green-50 text-green-600 border border-green-100" : "bg-purple-50 text-purple-600 border border-purple-100"
                            }`}>
                              {m.match_score}% Match
                            </span>
                          </div>

                          {/* Connection profile description */}
                          <div className="space-y-2 flex-grow">
                            <div className="flex items-center gap-2">
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center font-bold text-sm shrink-0 shadow-xs">
                                {m.contact.name.slice(0, 2).toUpperCase()}
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <h4 className="font-display font-semibold text-sm text-gray-900">{m.contact.name}</h4>
                                  {m.contact.linkedin_url && (
                                    <a 
                                      href={m.contact.linkedin_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer" 
                                      className="text-[#0A66C2] hover:text-[#004182] transition"
                                    >
                                      <Linkedin className="w-3.5 h-3.5" />
                                    </a>
                                  )}
                                </div>
                                <p className="text-[10px] text-gray-400 font-medium leading-none">{m.contact.headline}</p>
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-1">
                              {m.contact.is_investor && <span className="text-[8px] uppercase font-bold bg-amber-50 text-amber-600 border border-amber-100 px-2 py-0.5 rounded">Investor</span>}
                              {m.contact.is_founder && <span className="text-[8px] uppercase font-bold bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded">Founder</span>}
                              {m.contact.is_advisor && <span className="text-[8px] uppercase font-bold bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded">Advisor</span>}
                              {m.contact.skills.slice(0, 3).map(sk => (
                                <span key={sk} className="text-[8px] font-semibold bg-gray-50 border border-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{sk}</span>
                              ))}
                            </div>

                            {/* Matching logic details breakdown */}
                            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 space-y-1.5 text-[10px] text-gray-600 font-medium">
                              <p className="text-[#7C3AED] font-semibold">Why Recommended / Relevant:</p>
                              <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-500">
                                {m.reasons.map((re, idx) => (
                                  <li key={idx}>{re}</li>
                                ))}
                              </ul>
                              <p className="mt-1"><span className="text-gray-700 font-semibold">How they can help:</span> {m.can_help}</p>
                              <p><span className="text-gray-700 font-semibold">Expected value:</span> {m.expected_value}</p>
                              <p><span className="text-gray-700 font-semibold">Suggested discussion topics:</span> {m.suggested_topics.join(", ")}</p>
                              
                              <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1.5 border-t border-gray-200/60 mt-1 text-[9px] text-gray-500 font-semibold">
                                <span className="flex items-center gap-1">
                                  <Network className="w-3 h-3 text-gray-400" /> Connection Strength: <strong className="text-gray-700">{m.contact.connection_strength || 50}%</strong>
                                </span>
                                <span className="flex items-center gap-1">
                                  <Sparkles className="w-3 h-3 text-[#7C3AED]" /> Priority: <strong className={
                                    m.confidence_level === "High" ? "text-green-600" : m.confidence_level === "Medium" ? "text-amber-600" : "text-red-500"
                                  }>{m.confidence_level}</strong>
                                </span>
                              </div>
                            </div>

                            {m.suggested_outreach && (
                              <div className="mt-3 p-3 bg-gray-50 border border-gray-100 rounded-xl">
                                <p className="text-[#7C3AED] font-semibold text-[9px] uppercase tracking-wider mb-1 flex items-center gap-1">
                                  <Mail className="w-3 h-3 text-[#7C3AED]" /> Suggested Outreach Message
                                </p>
                                <p className="text-[10px] text-gray-600 italic font-medium">"{m.suggested_outreach}"</p>
                              </div>
                            )}
                          </div>

                          {/* CTA Button Block */}
                          <div className="flex flex-col justify-end gap-1.5 shrink-0 md:w-36 text-center">
                            <button
                              type="button"
                              onClick={() => generateMessage(m)}
                              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-[11px] font-semibold py-2.5 px-3 rounded-xl transition shadow-xs flex items-center justify-center gap-1"
                            >
                              <Sparkles className="w-3 h-3" /> Pitch Generator
                            </button>
                          </div>
                        </motion.div>
                      </AnimatePresence>

                      {/* Navigation Carousel Controls */}
                      <div className="flex items-center justify-between bg-white border border-gray-100 rounded-2xl px-5 py-3.5 shadow-xs text-xs font-semibold text-gray-700">
                        <button
                          type="button"
                          onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                          disabled={safeIndex === 0}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed transition font-semibold"
                          data-testid="recommendation-prev-btn"
                        >
                          <ChevronLeft className="w-4 h-4" /> Previous
                        </button>

                        <div className="flex items-center gap-2 text-xs font-bold text-gray-800 bg-purple-50/70 border border-purple-100/60 px-4 py-1.5 rounded-full">
                          <span className="text-[#7C3AED]">Recommendation</span>
                          <span className="text-gray-900">{safeIndex + 1}</span>
                          <span className="text-gray-400 font-normal">of</span>
                          <span className="text-gray-900">{filteredMatches.length}</span>
                        </div>

                        <button
                          type="button"
                          onClick={() => setCurrentIndex((prev) => Math.min(filteredMatches.length - 1, prev + 1))}
                          disabled={safeIndex >= filteredMatches.length - 1}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white disabled:opacity-40 disabled:hover:bg-[#7C3AED] disabled:cursor-not-allowed transition font-semibold shadow-xs"
                          data-testid="recommendation-next-btn"
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}

          {/* AI Suggestions when matching results are empty or low */}
          {insights && insights.total > 0 && filteredMatches.length < 3 && (
            <div className="bg-white border border-gray-100 rounded-[32px] p-5 shadow-xs text-xs font-medium text-gray-700 space-y-2">
              <h3 className="font-display font-semibold text-sm text-gray-900 flex items-center gap-1.5">
                <Info className="w-4.5 h-4.5 text-[#7C3AED]" /> Suggested Network Expansion
              </h3>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                If the perfect contact wasn't identified in your current imports, our AI models recommend connecting with professionals matching:
              </p>
              <div className="flex flex-wrap gap-1.5 text-[10px] font-semibold">
                <span className="bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded">Clinical Hospital Directors</span>
                <span className="bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded">Bioinformatics VCs</span>
                <span className="bg-purple-50 text-purple-600 border border-purple-100 px-2 py-0.5 rounded">Boston MedTech Hub mentors</span>
              </div>
            </div>
          )}
        </div>
      </div>
    )}

      {/* Outreach Message Generation Overlay Panel / Drawer */}
      <AnimatePresence>
        {drawerOpen && selectedMatch && (
          <div className="fixed inset-0 z-50 overflow-hidden flex justify-end">
            {/* Backdrop */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-xs"
            />

            {/* Drawer Body */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full max-w-md bg-white border-l border-gray-100 shadow-2xl flex flex-col justify-between z-10"
            >
              {/* Header */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-display font-semibold text-lg text-gray-900">Pitch Generator</h3>
                  <p className="text-[10px] text-gray-400">Generate personalized outreach message for {selectedMatch.contact.name}</p>
                </div>
                <button 
                  onClick={() => setDrawerOpen(false)}
                  className="text-gray-400 hover:text-gray-600 font-semibold text-lg px-2"
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="flex-grow overflow-y-auto p-5 space-y-4 text-xs font-semibold text-gray-700">
                {/* Channel Presets Selector */}
                <div>
                  <label className="block mb-1 text-gray-700">Channel Type</label>
                  <div className="grid grid-cols-3 gap-1.5 bg-gray-50 p-1 rounded-xl">
                    {[
                      { type: "LinkedIn", icon: Linkedin },
                      { type: "Email", icon: Mail },
                      { type: "Meeting Request", icon: Calendar }
                    ].map((chan) => (
                      <button
                        key={chan.type}
                        type="button"
                        onClick={() => setOutreachChannel(chan.type)}
                        className={`py-1.5 text-[10px] rounded-lg text-center transition flex items-center justify-center gap-1 font-bold ${
                          outreachChannel === chan.type
                            ? "bg-white text-[#7C3AED] shadow-xs"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        <chan.icon className="w-3 h-3" /> {chan.type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tone Selector */}
                <div>
                  <label className="block mb-1 text-gray-700">Tone Preset</label>
                  <div className="grid grid-cols-3 gap-1 bg-gray-50 p-1 rounded-xl">
                    {["Professional", "Friendly", "Investor Pitch", "Technical", "Short", "Formal"].map((tn) => (
                      <button
                        key={tn}
                        type="button"
                        onClick={() => setOutreachTone(tn)}
                        className={`py-1 text-[9px] rounded-lg text-center transition font-bold ${
                          outreachTone === tn
                            ? "bg-white text-[#7C3AED] shadow-xs"
                            : "text-gray-500 hover:text-gray-800"
                        }`}
                      >
                        {tn}
                      </button>
                    ))}
                  </div>
                </div>

                {/* AI generated text output block */}
                <div className="space-y-1.5">
                  <label className="block text-gray-700">Generated Outreach Message</label>
                  
                  {generatingMessage ? (
                    /* Loading Skeleton */
                    <div className="w-full bg-gray-50 rounded-2xl p-4 border border-gray-150 min-h-[180px] space-y-2.5 animate-pulse">
                      <div className="h-3.5 bg-gray-200 rounded w-1/3" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-5/6" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-2/3" />
                    </div>
                  ) : (
                    <textarea
                      value={generatedMessage}
                      onChange={(e) => setGeneratedMessage(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 rounded-2xl p-4 outline-none min-h-[220px] font-medium text-gray-800 leading-relaxed text-[11px]"
                    />
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-5 border-t border-gray-100 bg-gray-50/50 flex justify-end gap-2 shrink-0">
                <button
                  onClick={copyMessage}
                  disabled={generatingMessage}
                  className="bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold px-4.5 py-2.5 rounded-xl transition inline-flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Message
                </button>
                <button
                  onClick={exportMessage}
                  disabled={generatingMessage}
                  className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold px-5 py-2.5 rounded-xl transition inline-flex items-center gap-1.5 shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" /> Export File
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
